import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate } from '../../../../utils/firebase-timestamp-transformation';
import './ClaimsTab.scss';

const ClaimsTab = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [migratedCount, setMigratedCount] = useState(0);

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      
      // Access the claims collection
      const claimsCollectionRef = collection(db, 'claims');
      const querySnapshot = await getDocs(claimsCollectionRef);
      
      const claimsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setClaims(claimsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching claims:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate all claims
  const handleMigrateAll = async () => {
    if (!window.confirm(`Are you sure you want to migrate all claims? This will update email addresses in the claims collection.`)) {
      return;
    }
    
    try {
      setMigrating(true);
      
      // Find claims that need migration
      const claimsToMigrate = claims.filter(claim => {
        // Skip already migrated claims
        if (claim.migrated) return false;
        
        // Check if email has mapping
        return claim.email && findNewEmail(claim.email);
      });
      
      setProgress({ current: 0, total: claimsToMigrate.length });
      
      // Initialize batch
      let batch = writeBatch(db);
      let batchCount = 0;
      let totalMigrated = 0;
      
      // Process claims in batches (Firestore has a limit of 500 operations per batch)
      for (let i = 0; i < claimsToMigrate.length; i++) {
        const claim = claimsToMigrate[i];
        const oldEmail = claim.email;
        const newEmail = findNewEmail(oldEmail);
        
        if (newEmail) {
          const claimRef = doc(db, 'claims', claim.id);
          
          batch.update(claimRef, {
            email: newEmail,
            oldEmail: oldEmail,
            migrated: true,
            migratedAt: new Date()
          });
          
          batchCount++;
          totalMigrated++;
          
          // If we've reached the batch limit, commit and start a new batch
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
        
        // Update progress
        setProgress({ current: i + 1, total: claimsToMigrate.length });
      }
      
      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setMigratedCount(totalMigrated);
      alert(`Successfully migrated ${totalMigrated} claim records.`);
      
      // Refresh the list
      fetchClaims();
      
    } catch (error) {
      console.error('Error migrating claims:', error);
      alert(`Error migrating claims: ${error.message}`);
    } finally {
      setMigrating(false);
    }
  };

  const filteredClaims = searchTerm 
    ? claims.filter(claim => 
        claim.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.empID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.expensePurpose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.status?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : claims;

  // Format date for display using the utility function
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const convertedDate = timestampToDate(date);
    return convertedDate ? convertedDate.toLocaleDateString() : 'N/A';
  };

  // Format currency amount
  const formatAmount = (amount, currency) => {
    if (amount === undefined || amount === null) return 'N/A';
    return `${currency || ''}${amount.toFixed(2)}`;
  };

  // Get filename from URL (reserved for display)
  // eslint-disable-next-line no-unused-vars
  const getFileName = (fileUrl) => {
    if (!fileUrl) return 'N/A';
    try {
      // Extract filename from Firebase Storage URL
      const urlParts = fileUrl.split('/');
      const fileWithParams = urlParts[urlParts.length - 1];
      const fileName = fileWithParams.split('?')[0];
      // Decode URI component to handle special characters
      return decodeURIComponent(fileName);
    } catch (error) {
      return fileUrl.substring(fileUrl.lastIndexOf('/') + 1, fileUrl.indexOf('?'));
    }
  };

  // Count total number of migratable claims
  const getMigratableCounts = () => {
    const total = claims.length;
    const alreadyMigrated = claims.filter(claim => claim.migrated).length;
    const canBeMigrated = claims.filter(claim => 
      !claim.migrated && 
      claim.email && 
      findNewEmail(claim.email)
    ).length;
    const noMapping = total - alreadyMigrated - canBeMigrated;
    
    return { total, alreadyMigrated, canBeMigrated, noMapping };
  };

  const counts = getMigratableCounts();

  return (
    <div className="claims-tab">
      <div className="claims-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by email, name, title, employee ID, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchClaims}
            disabled={loading || migrating}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="migration-control">
        <div className="stats">
          <div className="stat-item">
            <span className="stat-label">Total Claims:</span>
            <span className="stat-value">{counts.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Already Migrated:</span>
            <span className="stat-value">{counts.alreadyMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ready to Migrate:</span>
            <span className="stat-value">{counts.canBeMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">No Email Mapping:</span>
            <span className="stat-value">{counts.noMapping}</span>
          </div>
        </div>
        
        <button 
          className="migrate-all-button"
          onClick={handleMigrateAll}
          disabled={loading || migrating || counts.canBeMigrated === 0}
        >
          {migrating ? 'Migrating...' : 'Migrate All Claims'}
        </button>
      </div>
      
      {migrating && (
        <div className="progress-bar-container">
          <div className="progress-text">
            Migrating {progress.current} of {progress.total} claims...
          </div>
          <div className="progress-bar-wrapper">
            <div 
              className="progress-bar" 
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {migratedCount > 0 && !migrating && (
        <div className="success-message">
          Successfully migrated {migratedCount} claim records in the last operation.
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading claim records...</p>
        </div>
      ) : filteredClaims.length > 0 ? (
        <div className="claims-table-container">
          <table className="claims-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Title</th>
                <th>Amount</th>
                <th>Invoice</th>
                <th>Dates</th>
                <th>Purpose</th>
                <th>Status</th>
                <th>Document</th>
                <th>Migration Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredClaims.map(claim => (
                <tr key={claim.id} className={claim.migrated ? 'migrated-row' : ''}>
                  <td>
                    <div className="employee-name">{claim.name || 'N/A'}</div>
                    <div className="employee-id">{claim.empID || 'N/A'}</div>
                    <div className="employee-email">
                      {claim.email || 'N/A'}
                      {claim.migrated && (
                        <div>
                          <span className="migrated-badge">Migrated</span>
                          <div className="old-email">Old: {claim.oldEmail}</div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{claim.title || 'N/A'}</td>
                  <td>{formatAmount(claim.amount, claim.currency)}</td>
                  <td>{claim.invoiceNo || 'N/A'}</td>
                  <td>
                    <div>Claim: {formatDate(claim.claimDate)}</div>
                    <div>Expense: {formatDate(claim.expenseDate)}</div>
                  </td>
                  <td>{claim.expensePurpose || 'N/A'}</td>
                  <td>
                    <span className={`claim-status ${claim.status?.toLowerCase()}`}>
                      {claim.status || 'N/A'}
                    </span>
                  </td>
                  <td className="file-cell">
                    {claim.supportingDoc ? (
                      <a href={claim.supportingDoc} target="_blank" rel="noopener noreferrer">
                        View Doc
                      </a>
                    ) : 'N/A'}
                  </td>
                  <td>
                    {claim.migrated ? (
                      <span className="already-migrated">Migrated</span>
                    ) : findNewEmail(claim.email) ? (
                      <span className="can-migrate">Ready to Migrate</span>
                    ) : (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No claim records found.</p>
        </div>
      )}
    </div>
  );
};

export default ClaimsTab; 